import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";

export const RADAR_QUERY_KEY = ["radar", "matches"];

/* ───── API client ───── */
export const radarApi = {
  matches: async () => {
    const r = await api.get("/radar/matches", {
      validateStatus: (s) => s === 200 || s === 409,
    });
    if (r.status === 409) {
      const err = new Error("location_required");
      err.code = "location_required";
      err.status = 409;
      throw err;
    }
    return r.data;
  },
  setLocation: ({ latitude, longitude }) =>
    api.put("/location/me", { latitude, longitude }).then((r) => r.data),
  clearLocation: () => api.delete("/location/me").then((r) => r.data),
};

/* ───── Hooks ───── */
export function useMatches() {
  return useQuery({
    queryKey: RADAR_QUERY_KEY,
    queryFn: radarApi.matches,
    retry: (count, err) => err?.code !== "location_required" && count < 1,
    staleTime: 15_000,
  });
}

export function useSetLocation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: radarApi.setLocation,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: RADAR_QUERY_KEY });
    },
  });
}

export function useClearLocation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: radarApi.clearLocation,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: RADAR_QUERY_KEY });
    },
  });
}

/* ───── Browser geolocation wrapper ───── */
export function getBrowserLocation(options = {}) {
  return new Promise((resolve, reject) => {
    if (typeof navigator === "undefined" || !navigator.geolocation) {
      reject(new Error("Geolocation is not supported by this browser."));
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) =>
        resolve({
          latitude: pos.coords.latitude,
          longitude: pos.coords.longitude,
        }),
      (err) => {
        let message;
        switch (err.code) {
          case 1:
            message =
              "Location permission denied. Enable location for this site in your browser settings and try again.";
            break;
          case 2:
            message = "We couldn't determine your location. Try moving to a spot with better signal.";
            break;
          case 3:
            message = "Locating timed out. Please try again.";
            break;
          default:
            message = err.message || "Couldn't read your location.";
        }
        reject(new Error(message));
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 0, ...options }
    );
  });
}

/* ───── Formatting helpers ───── */
export function formatDistance(m) {
  if (m == null) return "";
  if (m < 1000) return `${Math.round(m)} m`;
  return `${(m / 1000).toFixed(1)} km`;
}

export function timeAgo(isoOrDate) {
  if (!isoOrDate) return "Just now";
  const d = typeof isoOrDate === "string" ? new Date(isoOrDate) : isoOrDate;
  const ms = Date.now() - d.getTime();
  if (Number.isNaN(ms) || ms < 0) return "Just now";
  const min = Math.floor(ms / 60_000);
  if (min < 1) return "Just now";
  if (min < 60) return `${min} min ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr} h ago`;
  const day = Math.floor(hr / 24);
  return `${day}d ago`;
}
